import * as echarts from 'echarts/core';
import { LineChart, BarChart } from 'echarts/charts';
import { GridComponent, TooltipComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { processTourData, getDataset } from './dataProcessor.js';

// Register only what the dashboard draws so esbuild can tree-shake the rest.
// TooltipComponent pulls in the axis pointer it needs on its own.
echarts.use([LineChart, BarChart, GridComponent, TooltipComponent, CanvasRenderer]);

const INK = '#0a0a0a';
const BG  = 'rgb(250,250,247)';
const MONTHS = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];

const YEAR_PALETTE = {
  '2006': '#1f5fff', '2007': '#d11f2b', '2008': '#15a145', '2009': '#e8a417',
  '2010': '#8b3fc9', '2011': '#0aa6a6', '2012': '#0f172a', '2013': '#c026d3',
  '2014': '#2563eb', '2015': '#dc2626', '2016': '#16a34a', '2017': '#f59e0b',
  '2018': '#6d28d9', '2019': '#0891b2', '2020': '#1e293b', '2021': '#b91c1c',
  '2022': '#ef4444', '2023': '#059669', '2024': '#7c3aed', '2025': '#0284c7',
  '2026': '#ea580c',
};
const FALLBACK_PALETTE = ['#1f5fff', '#d11f2b', '#15a145', '#e8a417', '#8b3fc9', '#0aa6a6'];

// Two of the charts carry a `modes` array instead of a single build: a
// segmented switch in the header flips between the aggregate view and the
// peak (max) view. resolveView() flattens whichever mode is active back into
// the same shape a single-mode chart already has.
const CHART_CONFIGS = {
  'cumulative': {
    title: 'Cumulative distance by year',
    subtitle: 'Stepped cumulative · each tread is a ride',
    subtitleShort: 'Stepped per ride',
    unit: 'kilometres', unitShort: 'km',
    build: buildCumulative,
  },
  'monthly-distance': {
    unit: 'kilometres', unitShort: 'km',
    modes: [
      {
        id: 'total', label: 'Total',
        title: 'Monthly distance by year',
        subtitle: 'Kilometres ridden per month',
        subtitleShort: 'km per month',
        build: buildMonthlyDistance,
      },
      {
        id: 'max', label: 'Max',
        title: 'Monthly max distance by year',
        subtitle: 'Longest single ride per month',
        subtitleShort: 'longest ride per month',
        build: buildMonthlyMaxDistance,
      },
    ],
  },
  'monthly-speed': {
    unit: 'km/h', unitShort: 'km/h',
    modes: [
      {
        id: 'avg', label: 'Avg',
        title: 'Monthly avg speed by year',
        subtitle: 'Monthly distance over monthly ride time',
        subtitleShort: 'avg speed per month',
        build: buildMonthlyAvgSpeed,
      },
      {
        id: 'max', label: 'Max',
        title: 'Monthly max avg speed by year',
        subtitle: 'Fastest ride-average speed per month',
        subtitleShort: 'max avg speed per month',
        build: buildMonthlyMaxSpeed,
      },
    ],
  },
};

let chart = null;
let dataset = [];
let yearColors = {};
let offYears = new Set();
let currentTab = 'cumulative';
// Which mode each multi-mode chart last showed; defaults to the first mode.
const tabMode = { 'monthly-distance': 'total', 'monthly-speed': 'avg' };
let onYearsChangedCb = () => {};
let onTabChangeCb = () => {};
let onSelectionChangeCb = () => {};

export function initCharts({ onYearsChanged, onTabChange, onSelectionChange } = {}) {
  if (onYearsChanged) onYearsChangedCb = onYearsChanged;
  if (onTabChange) onTabChangeCb = onTabChange;
  if (onSelectionChange) onSelectionChangeCb = onSelectionChange;
  const dom = document.getElementById('chart');
  if (!dom) return;
  chart = echarts.init(dom);

  let lastNarrow = window.innerWidth < 600;
  const onResize = debounce(() => {
    chart?.resize();
    const narrow = window.innerWidth < 600;
    // Rebuild the option when the narrow flag flips so baseOption()'s
    // width-aware grid (tighter on mobile) kicks in.
    if (narrow !== lastNarrow) {
      lastNarrow = narrow;
      refresh();
    }
    fitTabs();
  }, 100);
  window.addEventListener('resize', onResize);

  // ECharts measures the container at init() and never re-measures on its own.
  // A window resize event isn't enough — modal dialogs suppressing scrollbars,
  // late font loads, or any parent reflow can change #chart's box without
  // firing one. Observe the container directly so the canvas stays in sync.
  if (typeof ResizeObserver !== 'undefined') {
    new ResizeObserver(() => chart?.resize()).observe(dom);
  }

  document.querySelectorAll('#tabs .tab').forEach(tab => {
    tab.addEventListener('click', () => selectTab(tab.dataset.tab));
  });

  const mobileSel = document.getElementById('tabs-mobile');
  if (mobileSel) mobileSel.addEventListener('change', e => selectTab(e.target.value));

  fitTabs();
  // Fonts may load after first paint — refit tabs and resize the chart so axis
  // label measurements use the real font metrics, not the fallback's.
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => {
      fitTabs();
      chart?.resize();
    }).catch(() => {});
  }
}

// ── tab fitting (measurement-based: A full → B abbreviated → C no ordinal) ──

function fitTabs() {
  const tabs = document.getElementById('tabs');
  if (!tabs) return;
  if (window.innerWidth < 600) return; // dropdown handles this range via CSS
  tabs.classList.remove('tabs--abbrev', 'tabs--no-idx');
  if (tabs.scrollWidth <= tabs.clientWidth) return;
  tabs.classList.add('tabs--abbrev');
  if (tabs.scrollWidth <= tabs.clientWidth) return;
  tabs.classList.add('tabs--no-idx');
}

function debounce(fn, ms) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
}

export function setRides(rides) {
  if (!rides || rides.length === 0) {
    dataset = [];
    yearColors = {};
    offYears = new Set();
    renderLegend();
    refresh();
    return;
  }
  const processed = processTourData({ tours: rides });
  dataset = getDataset(processed);
  yearColors = {};
  dataset.forEach((y, i) => {
    yearColors[y.id] = YEAR_PALETTE[y.id] ?? FALLBACK_PALETTE[i % FALLBACK_PALETTE.length];
  });
  // Keep any selection restored from storage (or set by the user this session)
  // but drop ids for years no longer in the data. New years default to visible.
  const known = new Set(dataset.map(y => y.id));
  offYears = new Set([...offYears].filter(id => known.has(id)));
  renderLegend();
  refresh();
}

// Seed the deselected-years set before the first setRides() so a persisted
// selection is applied as the data loads. No render here — dataset isn't ready.
export function restoreSelection(offIds) {
  offYears = new Set(offIds || []);
}

function persistSelection() {
  onSelectionChangeCb([...offYears]);
}

export function activeYears() {
  return dataset.filter(y => !offYears.has(y.id));
}

function selectTab(key) {
  // Active state covers every tab, including non-chart views (Rides).
  document.querySelectorAll('#tabs .tab').forEach(t => {
    t.classList.toggle('on', t.dataset.tab === key);
  });
  const mobileSel = document.getElementById('tabs-mobile');
  if (mobileSel && mobileSel.value !== key) mobileSel.value = key;

  if (CHART_CONFIGS[key]) {
    currentTab = key;
    renderHeader(key);
    refresh();
  }

  // Let the view router show/hide the matching section for this tab.
  onTabChangeCb(key);
}

// Flatten a chart key + its active mode into one view (title/subtitle/build).
// Single-mode charts carry those fields directly on the config.
function resolveView(key) {
  const cfg = CHART_CONFIGS[key];
  if (!cfg) return null;
  if (!cfg.modes) return cfg;
  const active = tabMode[key] ?? cfg.modes[0].id;
  return cfg.modes.find(m => m.id === active) ?? cfg.modes[0];
}

function renderHeader(key) {
  const view = resolveView(key);
  if (!view) return;
  const titleEl = document.getElementById('chart-title');
  const subEl = document.getElementById('chart-subtitle');
  if (titleEl) titleEl.textContent = view.title;
  // Emit both subtitle variants — CSS shows the one that fits the width.
  if (subEl) {
    subEl.innerHTML =
      `<span class="sub-full"></span><span class="sub-short"></span>`;
    subEl.querySelector('.sub-full').textContent  = view.subtitle;
    subEl.querySelector('.sub-short').textContent = view.subtitleShort;
  }
  renderToggle(key);
}

// Render the segmented aggregate/max switch, or hide it for single-mode charts.
function renderToggle(key) {
  const el = document.getElementById('chart-toggle');
  if (!el) return;
  const cfg = CHART_CONFIGS[key];
  if (!cfg || !cfg.modes) {
    el.innerHTML = '';
    el.classList.add('hidden');
    return;
  }
  const active = tabMode[key] ?? cfg.modes[0].id;
  el.innerHTML = '';
  cfg.modes.forEach(m => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'seg' + (m.id === active ? ' on' : '');
    btn.textContent = m.label;
    btn.setAttribute('aria-pressed', m.id === active ? 'true' : 'false');
    btn.addEventListener('click', () => setMode(key, m.id));
    el.appendChild(btn);
  });
  el.classList.remove('hidden');
}

function setMode(key, modeId) {
  if (tabMode[key] === modeId) return;
  tabMode[key] = modeId;
  renderHeader(key);
  refresh();
}

function refresh() {
  renderChart();
  renderChartMeta();
  onYearsChangedCb(activeYears(), dataset);
}

function renderChart() {
  if (!chart) return;
  const view = resolveView(currentTab);
  const option = view.build(activeYears());
  chart.setOption(option, true);
}

function renderChartMeta() {
  const meta = document.getElementById('chart-meta');
  if (!meta) return;
  const cfg = CHART_CONFIGS[currentTab];
  const visibleCount = activeYears().length;
  const seasonsFull  = visibleCount === 1 ? '1 season selected' : `${visibleCount} seasons selected`;
  const seasonsShort = visibleCount === 1 ? '1 season' : `${visibleCount} seasons`;
  // Full (two lines, desktop) and short (one line, mobile) — CSS swaps.
  meta.innerHTML = `
    <div class="meta-full">
      <div><b>${seasonsFull}</b></div>
      <div>unit is ${cfg.unit}</div>
    </div>
    <div class="meta-short"><b>${seasonsShort}</b> · ${cfg.unitShort}</div>
  `;
}

// ── legend ────────────────────────────────────────────────────────────────────

function renderLegend() {
  const legend = document.getElementById('legend');
  if (!legend) return;
  legend.innerHTML = '';

  dataset.forEach(y => {
    const item = document.createElement('div');
    item.className = 'leg' + (offYears.has(y.id) ? ' off' : '');
    item.dataset.year = y.id;
    item.innerHTML = `<span class="sw" style="background:${yearColors[y.id]}"></span>${y.id}`;
    item.addEventListener('click', () => toggleYear(y.id));
    item.addEventListener('mouseenter', () => {
      if (offYears.has(y.id)) return;
      applyEmphasis(y.id);
    });
    item.addEventListener('mouseleave', () => applyEmphasis(null));
    legend.appendChild(item);
  });

  if (dataset.length === 0) return;

  const ctrl = document.createElement('div');
  ctrl.className = 'legend-ctrl';
  ctrl.appendChild(ctrlButton('ALL', () => setAll(true)));
  ctrl.appendChild(ctrlButton('NONE', () => setAll(false)));
  ctrl.appendChild(ctrlButton('INVERT', invertAll));
  legend.appendChild(ctrl);
}

function ctrlButton(label, fn) {
  const s = document.createElement('span');
  s.textContent = label;
  s.addEventListener('click', fn);
  return s;
}

function toggleYear(yearId) {
  if (offYears.has(yearId)) offYears.delete(yearId);
  else offYears.add(yearId);
  syncLegendClasses();
  persistSelection();
  refresh();
}

function applyEmphasis(focusId) {
  if (!chart || currentTab !== 'cumulative') return;
  const series = activeYears().map(y => ({
    name: y.id,
    lineStyle: {
      color: yearColors[y.id],
      width: focusId && y.id === focusId ? 3 : 1.8,
      opacity: !focusId || y.id === focusId ? 1 : 0.15,
      cap: 'butt', join: 'miter',
    },
  }));
  chart.setOption({ series });
}

function setAll(on) {
  if (on) offYears.clear();
  else dataset.forEach(y => offYears.add(y.id));
  syncLegendClasses();
  persistSelection();
  refresh();
}

function invertAll() {
  dataset.forEach(y => {
    if (offYears.has(y.id)) offYears.delete(y.id);
    else offYears.add(y.id);
  });
  syncLegendClasses();
  persistSelection();
  refresh();
}

function syncLegendClasses() {
  document.querySelectorAll('#legend .leg').forEach(el => {
    el.classList.toggle('off', offYears.has(el.dataset.year));
  });
}

// ── option builders ───────────────────────────────────────────────────────────

function baseOption() {
  // Tighten the drawable grid at mobile widths so the chart doesn't look
  // like it's floating inside a ~50px dead-zone frame. containLabel lets
  // ECharts auto-reserve just enough room for axis labels — avoids hardcoding
  // a left gutter wide enough for "20.000".
  const narrow = window.innerWidth < 600;
  const grid = narrow
    ? { left: 2,  right: 8,  top: 8,  bottom: 2,  containLabel: true }
    : { left: 60, right: 24, top: 12, bottom: 42, containLabel: false };
  return {
    // The cumulative chart's data and min/max are UTC-midnight ISO date
    // strings. Without this, ECharts places time-axis ticks at *local*
    // month boundaries and the formatter's getUTCMonth() reads one tick
    // back, shifting every label by one month east of UTC.
    useUTC: true,
    animation: true,
    grid,
    tooltip: {
      trigger: 'axis',
      backgroundColor: INK, borderColor: INK, borderWidth: 0, padding: [8, 10],
      textStyle: { color: BG, fontFamily: 'JetBrains Mono, monospace', fontSize: 11 },
      axisPointer: { type: 'line', lineStyle: { color: INK, width: 1, opacity: 0.4 } },
    },
  };
}

function timeXAxis() {
  return {
    type: 'time',
    // Z suffix: ECharts parses bare YYYY-MM-DD as local time; useUTC only
    // affects formatting, so the parse/format skew shifts tooltip labels.
    min: '2000-01-01T00:00:00Z',
    max: '2000-12-31T00:00:00Z',
    boundaryGap: false,
    axisLine:  { lineStyle: { color: INK, width: 2 } },
    axisTick:  { show: true, length: 6, alignWithLabel: false, lineStyle: { color: INK, width: 1 } },
    axisLabel: {
      color: INK, fontFamily: 'JetBrains Mono, monospace', fontSize: 10, margin: 14,
      formatter: v => MONTHS[new Date(v).getUTCMonth()],
    },
    splitLine: { show: false },
  };
}

function categoryXAxis() {
  return {
    type: 'category', data: MONTHS, boundaryGap: true,
    axisLine:  { lineStyle: { color: INK, width: 2 } },
    axisTick:  { show: true, length: 6, alignWithLabel: true, lineStyle: { color: INK, width: 1 } },
    axisLabel: { color: INK, fontFamily: 'JetBrains Mono, monospace', fontSize: 10, margin: 14 },
    splitLine: { show: false },
  };
}

function yAxis({ min = 0, max, interval, formatter } = {}) {
  return {
    type: 'value', min, max, interval,
    axisLine:  { show: false },
    axisTick:  { show: false },
    splitLine: { show: true, lineStyle: { color: INK, type: 'dashed', opacity: 0.28, width: 1 } },
    axisLabel: {
      color: INK, fontFamily: 'JetBrains Mono, monospace', fontSize: 10, margin: 14,
      formatter: formatter ?? (v => v.toLocaleString()),
    },
  };
}

function niceCeil(value, step) {
  if (!value || value <= 0) return step;
  return Math.ceil(value / step) * step;
}

function lineSeries(y, data) {
  return {
    name: y.id, type: 'line', step: 'end', showSymbol: false, data,
    lineStyle: { color: yearColors[y.id], width: 1.8, cap: 'butt', join: 'miter' },
    itemStyle: { color: yearColors[y.id] },
    emphasis: { disabled: true },
  };
}

function barSeries(y, data) {
  return {
    name: y.id, type: 'bar', barMaxWidth: 40, data,
    itemStyle: {
      color: yearColors[y.id],
      borderColor: INK, borderWidth: 1, borderRadius: 0,
    },
    emphasis: { focus: 'series' },
  };
}

function buildCumulative(visible) {
  const maxVal = visible.reduce((m, y) => {
    const rowsMax = y.source.slice(1).reduce((mm, r) => Math.max(mm, r[5] || 0), 0);
    return Math.max(m, rowsMax);
  }, 0);
  const yMax = niceCeil(maxVal, 1000);
  return {
    ...baseOption(),
    xAxis: timeXAxis(),
    yAxis: yAxis({ max: yMax, interval: yMax / 5 }),
    // Z suffix forces ECharts to parse the bare date as UTC, not local.
    series: visible.map(y => lineSeries(y, y.source.slice(1).map(r => [r[1] + 'T00:00:00Z', r[5]]))),
  };
}

function buildMonthlyDistance(visible) {
  const maxVal = visible.reduce((m, y) => {
    const bucket = monthlyDistance(y);
    return Math.max(m, ...bucket.map(v => v || 0));
  }, 0);
  return {
    ...baseOption(),
    tooltip: { ...baseOption().tooltip, axisPointer: { type: 'shadow' } },
    xAxis: categoryXAxis(),
    yAxis: yAxis({ max: niceCeil(maxVal, 200) }),
    series: visible.map(y => barSeries(y, monthlyDistance(y))),
  };
}

function buildMonthlyMaxDistance(visible) {
  const maxVal = visible.reduce((m, y) => {
    const bucket = monthlyMaxDistance(y);
    return Math.max(m, ...bucket.map(v => v || 0));
  }, 0);
  return {
    ...baseOption(),
    tooltip: { ...baseOption().tooltip, axisPointer: { type: 'shadow' } },
    xAxis: categoryXAxis(),
    yAxis: yAxis({ max: niceCeil(maxVal, 50) }),
    series: visible.map(y => barSeries(y, monthlyMaxDistance(y))),
  };
}

function buildMonthlyMaxSpeed(visible) {
  const vals = visible.flatMap(y => monthlyMaxSpeed(y)).filter(v => v != null);
  const minVal = vals.length ? Math.floor(Math.min(...vals) - 2) : 0;
  const maxVal = vals.length ? Math.ceil(Math.max(...vals) + 2) : 50;
  return {
    ...baseOption(),
    tooltip: { ...baseOption().tooltip, axisPointer: { type: 'shadow' } },
    xAxis: categoryXAxis(),
    yAxis: yAxis({ min: Math.max(0, minVal), max: maxVal, formatter: v => v.toFixed(0) }),
    series: visible.map(y => barSeries(y, monthlyMaxSpeed(y))),
  };
}

function buildMonthlyAvgSpeed(visible) {
  const vals = visible.flatMap(y => monthlyAvgSpeed(y)).filter(v => v != null);
  const minVal = vals.length ? Math.floor(Math.min(...vals) - 2) : 0;
  const maxVal = vals.length ? Math.ceil(Math.max(...vals) + 2) : 50;
  return {
    ...baseOption(),
    tooltip: { ...baseOption().tooltip, axisPointer: { type: 'shadow' } },
    xAxis: categoryXAxis(),
    yAxis: yAxis({ min: Math.max(0, minVal), max: maxVal, formatter: v => v.toFixed(0) }),
    series: visible.map(y => barSeries(y, monthlyAvgSpeed(y))),
  };
}

// ── monthly aggregators (dataset source col indexes documented in dataProcessor.js) ──

function monthlyDistance(y) {
  const out = Array(12).fill(null);
  y.source.slice(1).forEach(row => {
    const month = row[2] - 1;
    out[month] = row[4];
  });
  return out.map(v => (v && v > 0 ? v : null));
}

function monthlyMaxDistance(y) {
  const out = Array(12).fill(null);
  y.source.slice(1).forEach(row => {
    const month = row[2] - 1;
    const d = row[3];
    if (d != null && d > 0) out[month] = Math.max(out[month] || 0, d);
  });
  return out;
}

function monthlyMaxSpeed(y) {
  const out = Array(12).fill(null);
  y.source.slice(1).forEach(row => {
    const month = row[2] - 1;
    const s = row[6];
    if (s && s > 0) out[month] = Math.max(out[month] || 0, s);
  });
  return out;
}

function monthlyAvgSpeed(y) {
  const out = Array(12).fill(null);
  y.source.slice(1).forEach(row => {
    const month = row[2] - 1;
    const cumDist = row[4];
    const cumDurHrs = row[8] / 3600;
    if (cumDist > 0 && cumDurHrs > 0) {
      out[month] = parseFloat((cumDist / cumDurHrs).toFixed(1));
    }
  });
  return out;
}
